import * as t from 'io-ts';

import type { MultiSelectDefinition } from 'header/types';

import { define, createSpecSettingsInterface } from './_common';
import type { Hierarchy } from '../../../services';

/**
 * This setting allows for connecting dimension or hierarchy selectors to multiple
 * hierarchies at the same time. If different hierarchies share a dimension (level),
 * their filters should synchronize.
 */
export interface LinkedHierarchiesSettingDefinition extends MultiSelectDefinition {
    key: 'LINKED_HIERARCHY_IDS';
    options: Array<{ key: string; label: string } & Hierarchy>;
}

const definition: LinkedHierarchiesSettingDefinition = {
    key: 'LINKED_HIERARCHY_IDS',
    type: 'MultiSelect',
    label: 'Linked hierarchies',
    value: [],
    options: [],
    scope: 'designer',
};

const specSettingsInterface = createSpecSettingsInterface(definition, t.array(t.string));

export const codec = specSettingsInterface.input;

export const LinkedHierarchiesSettingDefinition = define({
    key: 'LINKED_HIERARCHY_IDS',
    label: 'Linked hierarchies',
    definition,
    fromSetting: settings => specSettingsInterface.extractValue(settings.linkedHierarchies),
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: settings =>
        specSettingsInterface.createDefinitionWithSettings(settings.linkedHierarchies),
    applySettingDependency: props => {
        const { currentDefinition, diff } = props;
        const internalKeyChanged = !!diff.INTERNAL_KEY && !!diff.INTERNAL_KEY.previous;
        const hierarchyIdChanged = !!diff.HIERARCHY_ID && !!diff.HIERARCHY_ID.previous;

        if (hierarchyIdChanged || internalKeyChanged) return { value: [] };

        const currentContextHierarchies = diff.LINKED_HIERARCHY_IDS?.current;

        if (!internalKeyChanged || !currentContextHierarchies) return;

        const validValues = currentContextHierarchies.filter(id =>
            currentDefinition.options.some(({ key }) => key === id),
        );
        const allValuesStillValid = currentContextHierarchies.length === validValues.length;

        return allValuesStillValid ? undefined : { value: validValues };
    },
});
